import { useCallback, useState } from 'react';

import { Button, Icon, TextButton, Toast, useToast } from '@zillow/constellation-10';
import { IconDownloadOutline } from '@zillow/constellation-icons';

import { downloadFileFromUrl } from '../../../shared/utils/urlUtils';
import { logError } from '@zg-rentals/log-error';
import { DOWNLOAD_TOAST_MESSAGES, ZILLOW_LEASES_HELP } from '../../../shared/constants/stringConstants';

type Props = {
  fallbackFilename?: string;
  buttonLabel: string;
  url: string;
  onDownloadSuccess?: () => void;

  // Optional Constellation Button PropTypes
  className?: string;
  fluid?: boolean;
  buttonType?: string;
};

/*
  This is a download button
 */
function DownloadButton({
  fallbackFilename,
  url,
  buttonLabel,
  onDownloadSuccess,
  ...constellationButtonProps
}: Props): JSX.Element {
  const [isDownloading, setIsDownloading] = useState(false);
  const { enqueueToast } = useToast();

  const onFileDownloadedSuccessToast = useCallback(() => {
    enqueueToast(<Toast tone="success">{DOWNLOAD_TOAST_MESSAGES.LEASE_SUCCESS}</Toast>);
  }, [enqueueToast]);

  const onFileDownloadedErrorToast = useCallback(() => {
    enqueueToast(
      <Toast
        tone="critical"
        actionButton={
          <TextButton onImpact onClick={() => window.open(ZILLOW_LEASES_HELP, '_blank')}>
            Get lease help
          </TextButton>
        }
      >
        {DOWNLOAD_TOAST_MESSAGES.LEASE_ERROR}
      </Toast>,
    );
  }, [enqueueToast]);

  async function click(): Promise<void> {
    setIsDownloading(true);
    try {
      await downloadFileFromUrl(url, fallbackFilename);
      onFileDownloadedSuccessToast();
      setIsDownloading(false);
      if (onDownloadSuccess) {
        onDownloadSuccess();
      }
    } catch (err) {
      logError({ error: err, context: 'DownloadButton - click' });
      onFileDownloadedErrorToast();
      setIsDownloading(false);
    }
  }

  return (
    <Button
      tone="brand"
      emphasis="filled"
      loading={isDownloading}
      icon={
        <Icon>
          <IconDownloadOutline />
        </Icon>
      }
      {...constellationButtonProps}
      onClick={click}
    >
      {buttonLabel}
    </Button>
  );
}

export default DownloadButton;
